import { Plural, Trans } from "@lingui/macro";
import { IApiEpBillingProduct } from "@smp/api";
import {
  UiButton,
  UiDialogResponsive,
  UiFormControlLabel,
  UiRadio,
  UiRadioGroupUncustomized,
  UiTypography,
} from "@smp/ui";
import { getCurrencySign } from "@smp/utils";
import { IconCoins } from "@tabler/icons-react";
import clsx from "clsx";
import { FC, useCallback, useState } from "react";
import { useUpdateEffect } from "react-use";

import { useQueryBillingTokenPackageList } from "@/queries";
import { DialogPurchaseV2 } from "@/screens/app/components/dialogs/dialog_purchase_v2";

import classes from "./dialog_token_package.module.css";

export interface IDialogTokenPackageProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DialogTokenPackage: FC<IDialogTokenPackageProps> = ({
  isOpen,
  onClose,
}) => {
  const { data: packages, isLoading: isPackagesLoading } =
    useQueryBillingTokenPackageList();

  const [product, setProduct] = useState<IApiEpBillingProduct | undefined>();
  const [selectedPackageIdx, setSelectedPackageIdx] = useState(
    Math.floor((packages?.length || 0) / 2),
  );

  const handleClickConfirm = useCallback(() => {
    if (!packages?.length) {
      return;
    }

    setProduct(packages[selectedPackageIdx]);
  }, [packages, selectedPackageIdx]);

  const handleClosePurchase = useCallback(() => {
    setProduct(undefined);
    onClose();
  }, [onClose]);

  useUpdateEffect(() => {
    if (!packages?.length) {
      return;
    }

    setSelectedPackageIdx(Math.floor(packages.length / 2));
  }, [packages?.length, isOpen]);

  return (
    <>
      <UiDialogResponsive
        hidden={!!product}
        open={isOpen}
        title={<Trans>Buy Credits for X (Twitter)</Trans>}
        fullWidth
        maxWidth={360}
        isSmallPadding
        onClose={onClose}
        isLoading={isPackagesLoading}
      >
        <UiTypography>
          <Trans>
            Choose a token pack to power your automated&nbsp;posting.
          </Trans>
        </UiTypography>

        <UiRadioGroupUncustomized
          className={classes.radioGroup}
          onChange={(_e, value) => setSelectedPackageIdx(Number(value))}
          value={selectedPackageIdx}
        >
          {packages?.map((pack, idx) => (
            <UiFormControlLabel
              key={pack.id}
              control={
                <UiRadio color="primary" className={classes.radio__control} />
              }
              className={clsx(classes.radio, classes.radio_active)}
              classes={{
                label: classes.radio__label,
              }}
              label={
                <div className={classes.radio__container}>
                  <IconCoins className={classes.radio__icn} />
                  <div className={classes.radio__main}>
                    <UiTypography className={classes.radio__label}>
                      <Plural
                        zero={`# Tokens`}
                        one={`# Token`}
                        few={`# Tokens`}
                        many={`# Tokens`}
                        other={`# Tokens`}
                        value={pack.amount}
                      />
                    </UiTypography>

                    <UiTypography className={classes.radio__sublabel}>
                      {getCurrencySign(pack.currency)}
                      {pack.price}
                    </UiTypography>
                  </div>
                </div>
              }
              value={idx}
              labelPlacement="start"
            />
          ))}
        </UiRadioGroupUncustomized>

        <div className={classes.buttons}>
          <UiButton
            fullWidth
            variant="contained"
            size="large"
            onClick={handleClickConfirm}
          >
            <Trans>Confirm Purchase</Trans>
          </UiButton>

          <UiButton fullWidth variant="outlined" size="large" onClick={onClose}>
            <Trans>Cancel</Trans>
          </UiButton>
        </div>
      </UiDialogResponsive>

      {!!product && (
        <DialogPurchaseV2
          onClose={handleClosePurchase}
          onSuccess={handleClosePurchase}
          open={!!product}
          product={product}
        />
      )}
    </>
  );
};
